import {
  CONFIG_CALL_TO_ACTION_DEFAULT,
  CONFIG_FORM_CAPTURE_DEFAULT,
} from '../constants';
import { createSelector } from '@reduxjs/toolkit';
import {
  selectDetailProject,
  selectLanguageProjectSelected,
  selectListPostUsedOnTour,
} from './detailProjectSelectors';
import { getTextMultiLang, isServer } from '@utils/helper';
import { selectDefaultLanguageProject } from './multiLanguageSelector';
import { selectDomain } from './mainSelectors';

export const selectHotspotConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.hotspotConfig || undefined,
);

export const selectPolygonConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.polygonConfig || undefined,
);

export const selectThemeConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.themeConfig || undefined,
);

export const selectDollhouseConfigGlobal = createSelector(
  [selectDetailProject],
  project =>
    project?.config?.design?.dollhouseConfig
      ? {
          ...project?.config?.design?.dollhouseConfig,
          isShowOnStart:
            !!project?.config?.design?.dollhouseConfig?.isShowOnStart &&
            !!project.dollhouse?.obj_path,
        }
      : undefined,
);

export const selectPopupConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.popupConfig || undefined,
);

export const selectFormCaptureConfigGlobal = createSelector(
  [selectDetailProject],
  project => ({
    ...CONFIG_FORM_CAPTURE_DEFAULT,
    ...project?.config?.design?.formCaptureConfig,
  }),
);

export const selectCallToActionConfigGlobal = createSelector(
  [selectDetailProject],
  project => ({
    ...CONFIG_CALL_TO_ACTION_DEFAULT,
    ...project?.config?.design?.callToActionConfig,
  }),
);

export const selectMetaDataProject = createSelector(
  [
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
  ],
  (project, languageProjectSelected, defaultLanguageProject) =>
    project?.config?.marketing?.meta_data
      ? {
          ...project?.config?.marketing?.meta_data,
          title: getTextMultiLang(
            project?.config?.marketing?.meta_data?.title,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          description: getTextMultiLang(
            project?.config?.marketing?.meta_data?.description,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
        }
      : undefined,
);

export const selectGATrackingProject = createSelector(
  [selectDetailProject],
  project => project?.config?.marketing?.analytic?.uid || undefined,
);

export const selectGATagTrackingProject = createSelector(
  [selectDetailProject],
  project =>
    project?.config?.marketing?.analytic?.ggTag ||
    process.env.NEXT_PUBLIC_GA_TAG_DEFAULT ||
    undefined,
);

export const selectScriptChats = createSelector(
  [selectDetailProject],
  project => project?.config?.marketing?.script_chats || undefined,
);

export const selectConfigLittlePlanetProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.intro_little_planet || undefined,
);

export const selectConfigProjectionModeProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.projection_mode || undefined,
);

export const selectConfigControlModeProject = createSelector(
  [selectDetailProject],
  project =>
    project?.config?.setting?.control_mode || ({ mode: 'drag' } as const),
);

export const selectConfigFollowModeProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.follow_mode || { enable: true },
);

export const selectConfigFirstSceneProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.general?.first_scene || undefined,
);

export const selectConfigDisclaimeer = createSelector(
  [selectDetailProject, selectListPostUsedOnTour],
  (project, posts) =>
    project?.config?.setting?.general?.disclaimer
      ? posts.find(
          item => item.id === project?.config?.setting?.general?.disclaimer,
        ) || undefined
      : undefined,
);

export const selectLanguageProject = createSelector(
  [selectDomain, selectDetailProject],
  (state, project) => {
    return !isServer && window.languages
      ? state.languageProjectSelected &&
        state.languageProjectSelected.countryCode &&
        window.languages[state.languageProjectSelected.countryCode]
        ? state.languageProjectSelected.countryCode
        : project?.config?.setting?.general?.language &&
          window.languages[project.config.setting.general.language]
        ? project.config.setting.general.language
        : 'GB'
      : 'GB';
  },
);

export const selectDollhouseProject = createSelector(
  [selectDetailProject],
  project => project?.dollhouse,
);

export const selectInteractiveMapConfigProject = createSelector(
  [selectDetailProject],
  project => project?.interactivemap_config,
);
